import { useCallback, useEffect, useState } from 'react';
import {
  ACCOMPANIMENT_STATUS_LABELS,
  AS_ROADMAP_STEPS,
  ROADMAP_STEP_PROGRESS_LABELS,
  buildDefaultRoadmap,
  emptyEvaluation,
  formulaByKey,
  PROFILE_LEVEL_LABELS,
  resolveRoadmapSteps,
  roadmapStepsToObjectives,
  type AccompanimentActivity,
  type AccompanimentEvaluation,
  type AccompanimentStatus,
  type EntrepreneurialProfileLevel,
  type RoadmapObjective,
  type RoadmapStepProgress,
  type StrategicAccompaniment,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { ActivitiesPanel } from '@/features/accompagnement/ActivitiesPanel';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/accompagnement/accompagnement.css';

type AccListItem = StrategicAccompaniment & {
  user?: { first_name: string; last_name: string; email: string } | null;
  advisor?: { first_name: string; last_name: string; email: string } | null;
};

type Staff = { id: string; first_name: string; last_name: string; email: string; role: string };

function displayName(p?: { first_name: string; last_name: string; email: string } | null) {
  if (!p) return '—';
  return [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;
}

export function AdminAccompagnementPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<AccListItem[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activities, setActivities] = useState<AccompanimentActivity[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapObjective[]>([]);
  const [evaluation, setEvaluation] = useState<AccompanimentEvaluation>(() => emptyEvaluation());
  const [advisorPick, setAdvisorPick] = useState('');
  const [loading, setLoading] = useState(false);

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const load = useCallback(async () => {
    const [accRes, staffRes] = await Promise.all([
      supabase.from('strategic_accompagnements').select('*').order('updated_at', { ascending: false }),
      supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role')
        .in('role', ['advisor', 'admin'])
        .eq('is_active', true),
    ]);
    if (accRes.error) {
      toast.error(accRes.error.message);
      return;
    }
    const list = (accRes.data ?? []) as AccListItem[];
    const userIds = [...new Set(list.map((r) => r.user_id))];
    const advisorIds = [...new Set(list.map((r) => r.advisor_id).filter(Boolean))] as string[];
    const ids = [...new Set([...userIds, ...advisorIds])];
    let profilesMap: Record<string, Staff> = {};
    if (ids.length) {
      const { data: ps } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, role')
        .in('id', ids);
      for (const p of ps ?? []) profilesMap[p.id] = p as Staff;
    }
    setRows(
      list.map((r) => ({
        ...r,
        user: profilesMap[r.user_id] ?? null,
        advisor: r.advisor_id ? profilesMap[r.advisor_id] ?? null : null,
      })),
    );
    setStaff((staffRes.data ?? []) as Staff[]);
  }, [toast]);

  const loadDetail = useCallback(async (id: string) => {
    const { data: acc } = await supabase.from('strategic_accompagnements').select('*').eq('id', id).maybeSingle();
    if (acc) {
      const raw = ((acc.roadmap as RoadmapObjective[]) ?? []).map((o, i) => ({
        ...o,
        order: o.order ?? i + 1,
      }));
      const views = resolveRoadmapSteps(raw.length ? raw : null, acc.formula_key);
      setRoadmap(roadmapStepsToObjectives(views.length ? views : resolveRoadmapSteps(buildDefaultRoadmap(acc.formula_key), acc.formula_key)));
      setEvaluation({ ...emptyEvaluation(), ...((acc.evaluation as AccompanimentEvaluation) || {}) });
    }
    const { data: acts } = await supabase
      .from('accompaniment_activities')
      .select('*')
      .eq('accompaniment_id', id)
      .order('sort_order', { ascending: true });
    setActivities((acts ?? []) as AccompanimentActivity[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    setAdvisorPick(selected?.advisor_id ?? '');
  }, [selected?.advisor_id, selectedId]);

  async function assignAdvisor(accId: string, advisorId: string) {
    if (!advisorId) return;
    setLoading(true);
    try {
      const { error: err } = await supabase
        .from('strategic_accompagnements')
        .update({
          advisor_id: advisorId,
          assigned_at: new Date().toISOString(),
          status: 'roadmap',
        })
        .eq('id', accId);
      if (err) throw err;
      toast.success('Conseiller assigné — phase feuille de route.', 'Accompagnement');
      await load();
      setSelectedId(accId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Assignation impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveRoadmap(validate: boolean) {
    if (!selected) return;
    setLoading(true);
    try {
      const cleaned = roadmapStepsToObjectives(
        resolveRoadmapSteps(roadmap, selected.formula_key),
      ).map((o) => ({
        ...o,
        title: (o.title || '').trim() || AS_ROADMAP_STEPS.find((s) => s.key === o.step_key)?.title || 'Étape',
      }));
      const patch: Record<string, unknown> = { roadmap: cleaned };
      if (validate) {
        patch.roadmap_validated_at = new Date().toISOString();
        patch.status = 'activities';
      } else if (selected.status === 'roadmap' || selected.status === 'engaged') {
        patch.status = 'roadmap';
      }
      const { error: err } = await supabase.from('strategic_accompagnements').update(patch).eq('id', selected.id);
      if (err) throw err;
      toast.success(
        validate ? 'Feuille de route validée — activités ouvertes.' : 'Feuille de route enregistrée.',
        'Accompagnement',
      );
      await load();
      await loadDetail(selected.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function openEvaluation() {
    if (!selected) return;
    setLoading(true);
    try {
      const { error: err } = await supabase
        .from('strategic_accompagnements')
        .update({ status: 'evaluation' })
        .eq('id', selected.id);
      if (err) throw err;
      toast.success('Phase évaluation ouverte.', 'Accompagnement');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveEvaluation(complete: boolean) {
    if (!selected || !profile) return;
    setLoading(true);
    try {
      const payload: AccompanimentEvaluation = {
        ...evaluation,
        completed_at: complete ? new Date().toISOString() : evaluation.completed_at,
        completed_by: profile.id,
      };
      const patch: Record<string, unknown> = { evaluation: payload };
      if (complete) patch.status = 'completed';
      else patch.status = 'evaluation';
      const { error: err } = await supabase.from('strategic_accompagnements').update(patch).eq('id', selected.id);
      if (err) throw err;
      toast.success(
        complete ? 'Évaluation finalisée — résultats disponibles.' : 'Évaluation enregistrée.',
        'Accompagnement',
      );
      await load();
      await loadDetail(selected.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <section className="panel" style={{ marginBottom: '1rem' }}>
        <p className="muted" style={{ marginTop: 0 }}>
          Assigner un conseiller, co-construire la feuille de route, suivre les activités et clôturer
          par l’évaluation.
        </p>
      </section>

      <div className="grid-2">
        <section className="panel">
          <h3 style={{ marginTop: 0 }}>Parcours ({rows.length})</h3>
          {rows.length === 0 ? (
            <p className="muted">Aucun accompagnement démarré.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Abonné</th>
                    <th>Statut</th>
                    <th>Profil</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ background: r.id === selectedId ? 'var(--bg-deep)' : undefined }}>
                      <td>{displayName(r.user)}</td>
                      <td>
                        <span className="chip">{ACCOMPANIMENT_STATUS_LABELS[r.status as AccompanimentStatus]}</span>
                      </td>
                      <td>
                        {r.profile_level
                          ? `${PROFILE_LEVEL_LABELS[r.profile_level as EntrepreneurialProfileLevel]} (${r.profile_score})`
                          : '—'}
                      </td>
                      <td>
                        <button type="button" className="btn btn-ghost" onClick={() => setSelectedId(r.id)}>
                          Ouvrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel">
          {!selected ? (
            <p className="muted">Sélectionnez un parcours pour le piloter.</p>
          ) : (
            <div>
              <h3 style={{ marginTop: 0 }}>{displayName(selected.user)}</h3>
              <p>
                Statut : <strong>{ACCOMPANIMENT_STATUS_LABELS[selected.status]}</strong>
                {selected.formula_key ? (
                  <>
                    {' '}
                    — Formule : <strong>{formulaByKey(selected.formula_key)?.label ?? selected.formula_key}</strong>
                  </>
                ) : null}
              </p>
              {selected.expected_results ? (
                <p className="muted">Résultats attendus : {selected.expected_results}</p>
              ) : null}
              {selected.profile_summary ? <p className="muted">{selected.profile_summary}</p> : null}

              <div className="field" style={{ marginTop: '1rem' }}>
                <label>Conseiller</label>
                {selected.advisor_id ? (
                  <p style={{ marginTop: 0 }}>
                    Actuel : <strong>{displayName(selected.advisor)}</strong>
                  </p>
                ) : null}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <select value={advisorPick} onChange={(e) => setAdvisorPick(e.target.value)}>
                    <option value="">— Choisir —</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {displayName(s)} ({s.role})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={
                      loading ||
                      !advisorPick ||
                      selected.status === 'profiling' ||
                      selected.status === 'ready_to_engage'
                    }
                    onClick={() => void assignAdvisor(selected.id, advisorPick)}
                  >
                    {selected.advisor_id ? 'Réassigner' : 'Assigner'}
                  </button>
                </div>
                {selected.status === 'profiling' || selected.status === 'ready_to_engage' ? (
                  <p className="muted">L’abonné doit d’abord s’engager formellement.</p>
                ) : null}
              </div>
            </div>
          )}
        </section>
      </div>

      {selected && (selected.status === 'roadmap' || selected.status === 'activities' || selected.status === 'evaluation' || selected.status === 'completed' || selected.advisor_id) ? (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Feuille de route (9 étapes)</h3>
          <p className="muted">
            Cochez les étapes incluses dans l’engagement{' '}
            <strong>{formulaByKey(selected.formula_key)?.label ?? selected.formula_key ?? '—'}</strong>. Les
            étapes décochées s’affichent côté abonné comme non applicables (croix rouge).
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Incluse</th>
                  <th>Étape</th>
                  <th>Statut</th>
                  <th>Avancement %</th>
                  <th>Échéance</th>
                </tr>
              </thead>
              <tbody>
                {AS_ROADMAP_STEPS.map((canon) => {
                  const o =
                    roadmap.find((x) => x.step_key === canon.key) ??
                    ({
                      id: canon.key,
                      order: canon.order,
                      step_key: canon.key,
                      title: canon.title,
                      description: canon.description,
                      target_date: '',
                      included: false,
                      status: 'upcoming' as RoadmapStepProgress,
                      progress: 0,
                    } satisfies RoadmapObjective);
                  return (
                    <tr key={canon.key} style={o.included === false ? { opacity: 0.65 } : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={o.included !== false}
                          disabled={loading}
                          onChange={(e) => {
                            const included = e.target.checked;
                            setRoadmap((prev) => {
                              const exists = prev.some((x) => x.step_key === canon.key);
                              if (exists) {
                                return prev.map((x) =>
                                  x.step_key === canon.key
                                    ? {
                                        ...x,
                                        included,
                                        status: included ? x.status || 'upcoming' : 'upcoming',
                                        progress: included ? x.progress || 0 : 0,
                                      }
                                    : x,
                                );
                              }
                              return [
                                ...prev,
                                {
                                  id: crypto.randomUUID?.() ?? `step-${canon.key}`,
                                  order: canon.order,
                                  step_key: canon.key,
                                  title: canon.title,
                                  description: canon.description,
                                  target_date: '',
                                  included,
                                  status: 'upcoming',
                                  progress: 0,
                                },
                              ];
                            });
                          }}
                        />
                      </td>
                      <td>
                        <strong>
                          {canon.order}. {canon.title}
                        </strong>
                        <div className="muted">{canon.description}</div>
                      </td>
                      <td>
                        <select
                          value={o.status || 'upcoming'}
                          disabled={loading || o.included === false}
                          onChange={(e) =>
                            setRoadmap((prev) =>
                              prev.map((x) =>
                                x.step_key === canon.key
                                  ? { ...x, status: e.target.value as RoadmapStepProgress }
                                  : x,
                              ),
                            )
                          }
                        >
                          {(Object.keys(ROADMAP_STEP_PROGRESS_LABELS) as RoadmapStepProgress[]).map((k) => (
                            <option key={k} value={k}>
                              {ROADMAP_STEP_PROGRESS_LABELS[k]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={o.progress ?? 0}
                          disabled={loading || o.included === false}
                          onChange={(e) =>
                            setRoadmap((prev) =>
                              prev.map((x) =>
                                x.step_key === canon.key
                                  ? { ...x, progress: Number(e.target.value) || 0 }
                                  : x,
                              ),
                            )
                          }
                          style={{ width: '5rem' }}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={o.target_date || ''}
                          disabled={loading || o.included === false}
                          onChange={(e) =>
                            setRoadmap((prev) =>
                              prev.map((x) =>
                                x.step_key === canon.key ? { ...x, target_date: e.target.value } : x,
                              ),
                            )
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={loading}
              onClick={() => setRoadmap(buildDefaultRoadmap(selected.formula_key))}
            >
              Réinitialiser selon la formule
            </button>
            <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void saveRoadmap(false)}>
              Enregistrer
            </button>
            <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void saveRoadmap(true)}>
              Présenter / valider la feuille de route
            </button>
          </div>
        </section>
      ) : null}

      {selected && (selected.status === 'activities' || selected.status === 'evaluation' || selected.status === 'completed' || selected.roadmap_validated_at) ? (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <ActivitiesPanel
            accompanimentId={selected.id}
            entrepreneurName={displayName(selected.user)}
            formulaKey={selected.formula_key}
            roadmap={roadmap}
            activities={activities}
            mode="admin"
            onChange={async () => {
              if (selected.status === 'roadmap') {
                await supabase.from('strategic_accompagnements').update({ status: 'activities' }).eq('id', selected.id);
              }
              await load();
              await loadDetail(selected.id);
            }}
            journeyStatusLabel={
              selected.status === 'completed' ? 'Terminé' : selected.status === 'evaluation' ? 'Évaluation' : 'En cours'
            }
          />
          {selected.status === 'activities' ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginTop: '1rem' }}
              disabled={loading}
              onClick={() => void openEvaluation()}
            >
              Passer à l’évaluation
            </button>
          ) : null}
        </section>
      ) : null}

      {selected && (selected.status === 'evaluation' || selected.status === 'completed') ? (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Évaluation & résultats</h3>
          <div className="grid-2">
            {(
              [
                ['progress_score', 'Progression'],
                ['impact_score', 'Impact'],
                ['satisfaction_score', 'Satisfaction'],
              ] as const
            ).map(([key, label]) => (
              <div className="field" key={key}>
                <label>
                  {label} (1–5)
                </label>
                <select
                  value={evaluation[key]}
                  disabled={selected.status === 'completed'}
                  onChange={(e) => setEvaluation({ ...evaluation, [key]: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {(
            [
              ['results_summary', 'Synthèse des résultats'],
              ['strengths', 'Points forts'],
              ['gaps', 'Écarts / axes d’amélioration'],
              ['next_steps', 'Prochaines étapes'],
            ] as const
          ).map(([key, label]) => (
            <div className="field" key={key}>
              <label>{label}</label>
              <textarea
                value={evaluation[key]}
                disabled={selected.status === 'completed'}
                onChange={(e) => setEvaluation({ ...evaluation, [key]: e.target.value })}
              />
            </div>
          ))}
          {selected.status !== 'completed' ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void saveEvaluation(false)}>
                Enregistrer brouillon
              </button>
              <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void saveEvaluation(true)}>
                Finaliser les résultats
              </button>
            </div>
          ) : (
            <p className="muted" style={{ marginBottom: 0 }}>
              <span className="chip">Parcours clôturé</span>
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
